import { takeEvery, all, call, put, select } from 'redux-saga/effects'

import * as actions from './actions'
import { clearCommonError } from '../Common/actions'
import { history } from '../../util/history'
import * as url from './url'
import { api } from '../api'
import { util } from '../../util'
import { constants } from '../../constants'

function* createPromoSaga(action) {
    try {
        yield put(actions.promoLoadingStart())
        const email = yield select(util.getUserEmail)
        const data = { ...action.payload, updated_by: email }
        const {
            data: { promotion },
        } = yield call(api, url.createPromoURL, 'POST', data)
        yield put(actions.createPromoSuccess(promotion))
        yield put(actions.modalClose())
        history.push({
            pathname: constants.routePaths.promo_details.path.replace(':promoId', promotion._id),
            state: { fromCreatePromoSaga: true },
        })
    } catch (e) {
        console.log('CREATE PROMO ERROR', e)
        if (e.data && e.data.message) {
            yield put(actions.createPromoFail(e.data.message))
            yield put(clearCommonError())
        }
        yield put(actions.promoLoadingEnd())
    }
}

function* deletePromoById(action) {
    try {
        yield put(actions.promoLoadingStart())
        const data = {
            _id: action.payload,
        }
        yield call(api, url.deletePromoURL, 'POST', data)
        yield put(actions.enablePromoAlert('Promo Successfully Deleted'))
        yield put(actions.promoLoadingEnd())
        history.push('/dashboard/promotions')
    } catch (e) {
        yield put(actions.promoLoadingEnd())
    }
}

function* deletePromos(action) {
    try {
        yield put(actions.promoLoadingStart())
        const { _id, ...rest } = action.payload
        const data = {
            _id: _id,
        }
        yield call(api, url.deletePromoURL, 'POST', data)
        const res = yield call(api, url.getPromosURL, 'POST', {
            ...rest,
        })
        yield put(actions.enablePromoAlert('Promo(s) Successfully Deleted'))
        yield put(
            actions.fetchPromoListSuccess({
                promotions_list: res.data.promotions,
                count: res.data.count,
                size: res.data.size,
                pageNo: res.data.pageNo,
            })
        )
    } catch (e) {
        yield put(actions.promoLoadingEnd())
    }
}

function* fetchPromoList(action) {
    try {
        const isSideMenuOpened = yield select(util.isSideMenuOpened)
        if (isSideMenuOpened) {
            return
        }
        yield put(actions.promoLoadingStart())
        const res = yield call(api, url.getPromosURL, 'POST', action.payload)
        if (history.location.pathname !== constants.routePaths.prioritize_promos.path) {
            /** updating global promotions table data */
            yield put(
                actions.fetchPromoListSuccess({
                    promotions_list: res.data.promotions,
                    count: res.data.count,
                    size: action.payload.size,
                    pageNo: res.data.pageNo,
                    order: action.payload.sort[Object.keys(action.payload.sort)[0]],
                    orderBy: Object.keys(action.payload.sort)[0],
                })
            )
        } else {
            /** updating the promotions_list for prioritize promos */
            yield put(
                actions.fetchPromoListSuccess({
                    promotions_list: res.data.promotions,
                })
            )
        }
    } catch (e) {
        yield put(actions.setPromoError(e.message))
        yield put(actions.promoLoadingEnd())
    }
}

function* fetchPrioritizePromos(action) {
    try {
        yield put(actions.promoLoadingStart())
        const res = yield call(api, url.getPromosURL, 'POST', action.payload)
        yield put(
            actions.fetchPrioritizePromosSuccess({
                prioritize_promos: res.data.promotions,
            })
        )
    } catch (e) {
        yield put(actions.promoLoadingEnd())
    }
}

function* updatePublishedPromo(updatingPromo, prevPromo) {
    let updatedPromo = prevPromo
    try {
        yield put(actions.promoLoadingStart())
        delete updatingPromo.promo.updated
        /** updating the promo */
        let {
            data: { promotion },
        } = yield call(api, url.updateMarketingURL, 'POST', updatingPromo)
        updatedPromo = promotion

        /** publishing the promo to QA */
        if (promotion.qa_is_live) {
            const config = {
                type: 'qa_live',
                publish: true,
                _id: promotion._id,
            }
            const publishQARes = yield call(api, url.publishContentURL, 'POST', config)
            updatedPromo = publishQARes.data.promotion
        }

        /** publishing the promo to PROD */
        if (promotion.is_live) {
            const config = {
                type: 'live',
                publish: true,
                _id: promotion._id,
            }
            const publishProdRes = yield call(api, url.publishContentURL, 'POST', config)
            updatedPromo = publishProdRes.data.promotion
        }
        return updatedPromo
    } catch (error) {
        console.error('Failing updating / publishing promo', error)
        /** to avoid common error modal pop up we are disabling the common error
         * and showing an alert message in edit prioritization and in prioritization section.
         */
        yield put(clearCommonError())
        yield put(actions.updatePromotionFailed())
        return { ...updatedPromo, isPromoUpdateFailed: true }
    }
}

function* updatePrioritizedPromos(action) {
    const email = yield select(util.getUserEmail)
    const prioritizePromos = yield select(util.getPrioritizePromos)

    const updatingMultiplePromos = action.payload.map(promo => {
        const data = util.promoUpdatedBy({ promo }, email)
        const prevPromo = prioritizePromos.find(({ _id }) => promo._id === _id)
        delete data.promo.isPromoUpdateFailed
        delete prevPromo.isPromoUpdateFailed
        return call(updatePublishedPromo, data, prevPromo)
    })

    /** updating and publishing each promo independently */
    let updatedPromos = yield all(updatingMultiplePromos)
    const isPromoUpdateFailed = yield select(util.getPrioritizationFailedStatus)

    /** combining updated prioritized promo with the remaining promos */
    updatedPromos = util.filterPromosById(prioritizePromos, updatedPromos)

    yield put(
        actions.fetchPrioritizePromosSuccess({
            prioritize_promos: updatedPromos,
            promo_loading: false,
            update_promotion_success: !isPromoUpdateFailed,
            update_promotion_failed: isPromoUpdateFailed,
        })
    )
}

function* fetchPromoById(action) {
    try {
        yield put(actions.promoLoadingStart())
        const data = { search: { _id: action.payload } }
        const res = yield call(api, url.getPromosURL, 'POST', data)
        const promo = res.data.promotions[0]
        delete promo.isConflictCheckReqd

        yield put(
            actions.fetchPromoSuccess({
                promo_details: promo,
            })
        )
    } catch (e) {
        console.log('fetch Promo By Id', e)
        yield put(actions.setPromoError(e.message))
        yield put(actions.promoLoadingEnd())
    }
}

function* clearAlert(action) {
    const delay = time => new Promise(resolve => setTimeout(resolve, time))
    yield call(delay, action.payload)
    yield put({ type: actions.actionTypes.CLEAR_DISPLAY_CONFIRMATION })
}

function* updateMarketing(action) {
    try {
        const prevPromo = yield select(util.getCurrentPromo)
        let editingPromo = yield select(util.getEditing)

        const { qa_is_live, is_live } = action.payload.promo
        const promo = yield qa_is_live || is_live
            ? updatePublishedPromo(action.payload, prevPromo)
            : updatePromo(action)

        const isPromoUpdateFailed = yield select(util.getPrioritizationFailedStatus)

        if (promo.status !== 'incomplete' && editingPromo) {
            editingPromo = false
        }

        yield put(
            actions.updateMarketingSuccess({
                promo_details: promo,
                is_editing_completed_promo: editingPromo,
                update_promotion_success: !isPromoUpdateFailed,
            })
        )
    } catch (e) {
        yield put(actions.setPromoError(e.message))
        yield put(actions.promoLoadingEnd())
    }
}

function* updatePromoName(action) {
    try {
        yield put(actions.promoLoadingStart())
        const email = yield select(util.getUserEmail)
        const data = util.promoUpdatedBy(action.payload, email)
        delete data.promo.updated
        const {
            data: { promotion },
        } = yield call(api, url.updateMarketingURL, 'POST', data)
        if (promotion) {
            yield put(actions.updateMarketingSuccess({ promo_details: promotion }))
            yield put(actions.modalClose())
        }
        history.push(constants.routePaths.promo_details.path.replace(':promoId', promotion._id))
    } catch (e) {
        if (e.data && e.data.message) {
            yield put(actions.updateMarketingFail(e.data.message))
            yield put(clearCommonError())
        }
        yield put(actions.promoLoadingEnd())
    }
}

function* updatePromo(action) {
    let prevPromo = yield select(util.getCurrentPromo)
    try {
        yield put(actions.promoLoadingStart())
        const email = yield select(util.getUserEmail)
        const data = util.promoUpdatedBy(action.payload, email)
        delete data.promo.updated
        const res = yield call(api, url.updateMarketingURL, 'POST', data)
        return res.data.promotion
    } catch (e) {
        yield put(actions.updateMarketingFail(e.data.message))
        yield put(actions.updatePromotionFailed())
        yield put(actions.promoLoadingEnd())
        return { ...prevPromo }
    }
}

function* updateInlineEdit(action) {
    try {
        const res = yield updatePromo(action)
        yield updatePromotionsList(res)
        yield put(actions.promoLoadingEnd())
    } catch (e) {
        console.log('updateInlineEdit', e)
        yield put(actions.promoLoadingEnd())
    }
}

function* fetchCategoriesList() {
    try {
        const res = yield call(api, url.getCategoriesListURL, 'GET')
        if (res) {
            yield put(actions.fetchCategoriesListSuccess(res.data.categories))
        }
    } catch (e) {
        console.log('fetchCategoriesList', e)
    }
}

function* publishContent(action) {
    try {
        const { publishContent, publishFromTable } = action.payload
        yield put(actions.promoLoadingStart())
        const res = yield call(api, url.publishContentURL, 'POST', publishContent)
        const promotion = res.data.promotion
        if (publishFromTable) {
            yield updatePromotionsList(promotion)
        } else {
            yield put(
                actions.fetchPromoSuccess({
                    promo_details: promotion,
                })
            )
        }
        const alertMsg = `${constants.publishContentAction[publishContent.type]} was successfully ${
            constants.publishContentStatus[publishContent.publish]
        }`
        yield put(actions.enablePromoAlert(alertMsg))
        yield put(actions.promoLoadingEnd())
    } catch (e) {
        console.log('publishContent', e)
        yield put(actions.promoLoadingEnd())
    }
}

function* updatePromotionsList(promotion) {
    const promoList = yield select(util.getPromotionsList)
    const updatedPromoList = promoList.map(promo => {
        let updatedPromo = { ...promo }
        if (promo._id === promotion._id) {
            updatedPromo = promotion
        }
        return updatedPromo
    })
    yield put(actions.updateInlineEditSuccess(updatedPromoList))
}

function* fetchPromoUsers() {
    try {
        yield put(actions.userLoadingStart())
        const {
            data: { created_by, updated_by },
        } = yield call(api, url.getUsersURL, 'GET')

        const filters = yield select(util.getPromotionsFilter)
        const updatedFilters = filters.map(filter => {
            const cloneFilter = { ...filter }
            if (cloneFilter.key === 'created_by') {
                cloneFilter['baseFilterList'] = created_by
            }
            if (cloneFilter.key === 'updated_by') {
                cloneFilter['baseFilterList'] = updated_by
            }
            return cloneFilter
        })
        yield put(actions.updatePromotionFilters(updatedFilters))
    } catch (error) {
        console.log('fetch promo users', error)
        yield put(actions.userLoadingEnd())
    }
}

export default function* watchPromotions() {
    yield all([
        takeEvery(actions.actionTypes.SAGA_FETCH_PROMO_USERS, fetchPromoUsers),
        takeEvery(actions.actionTypes.SAGA_CREATE_PROMO, createPromoSaga),
        takeEvery(actions.actionTypes.SAGA_FETCH_PROMOS, fetchPromoList),
        takeEvery(actions.actionTypes.SAGA_DELETE_PROMO, deletePromoById),
        takeEvery(actions.actionTypes.CLEAR_PROMO_ALERT, clearAlert),
        takeEvery(actions.actionTypes.SAGA_UPDATE_PROMO_MARKETING, updateMarketing),
        takeEvery(actions.actionTypes.SAGA_UPDATE_PROMO_NAME, updatePromoName),
        takeEvery(actions.actionTypes.SAGA_FETCH_PROMO, fetchPromoById),
        takeEvery(actions.actionTypes.SAGA_BULK_DELETE, deletePromos),
        takeEvery(actions.actionTypes.SAGA_UPDATE_INLINE_EDIT, updateInlineEdit),
        takeEvery(actions.actionTypes.SAGA_FETCH_CATEGORIES_LIST, fetchCategoriesList),
        takeEvery(actions.actionTypes.SAGA_FETCH_PRIORITIZE_PROMOS, fetchPrioritizePromos),
        takeEvery(actions.actionTypes.SAGA_PUBLISH_CONTENT, publishContent),
        takeEvery(actions.actionTypes.SAGA_UPDATE_PRIORITIZED_PROMOS, updatePrioritizedPromos),
    ])
}
